import React, { useEffect, useId, useRef, useState } from 'react';
import { Copy, Plus, X } from 'lucide-react';
import { tenantApi, type NotificationAttachmentMapping, type NotificationStepConfig, type WorkflowNotificationContentValidationResponse } from '../../services/tenantApi';
import { inboundSchemasApi, effectiveOutputSchema, effectiveRuntimeBindings, type SchemaSettings } from '../../services/inboundSchemasApi';
import { contentCatalog, exampleContentVariables, expandContent, insertContentToken, schemaContentPaths, updateMessageContent,
  validVariablePath, type ContentSource, type ContentStep, type ContentVariable } from './workflowNotificationContentHelpers';
import guide from '../../../../docs/workflow-notification-content-guide.md?raw';

export interface WorkflowNotificationContentProps {
  config: NotificationStepConfig;
  onChange: (config: NotificationStepConfig) => void;
  workflowId?: string | null;
  workflowIntegrationId?: string | null;
  sourceType: ContentSource;
  workflowSteps: ContentStep[];
  currentStepOrder: number;
  globalVariableNames?: string[];
  recipientAction?: React.ReactNode;
}
type Field = 'subjectTemplate' | 'bodyTemplate';
type InsertField = Field | 'toEmails' | 'ccEmails' | 'bccEmails';
const MAX_ATTACHMENTS = 10;
const MAX_ATTACHMENT_KB = 192;
const formatBytes = (bytes: number) => bytes < 1024 ? `${bytes} B` : `${(bytes / 1024).toFixed(1)} KB`;

export const WorkflowNotificationGuideContent: React.FC = () => <pre>{guide}</pre>;

export const WorkflowNotificationContent: React.FC<WorkflowNotificationContentProps> = ({ config, onChange, workflowId, workflowIntegrationId, sourceType, workflowSteps, currentStepOrder, globalVariableNames = [], recipientAction }) => {
  const id = useId();
  const subjectRef = useRef<HTMLTextAreaElement>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const recipientRefs = useRef<Record<'toEmails' | 'ccEmails' | 'bccEmails', HTMLInputElement | null>>({ toEmails: null, ccEmails: null, bccEmails: null });
  const selection = useRef({ field: 'bodyTemplate' as InsertField, start: 0, end: 0 });
  const [target, setTarget] = useState<InsertField>('bodyTemplate');
  const [settings, setSettings] = useState<SchemaSettings | null>(null);
  const [schemaState, setSchemaState] = useState('');
  const [schemaSource, setSchemaSource] = useState<'active' | 'draft'>('active');
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('All');
  const [manualPath, setManualPath] = useState('');
  const [payload, setPayload] = useState('');
  const [context, setContext] = useState('');
  const [copyState, setCopyState] = useState('');
  const [validationRunning, setValidationRunning] = useState(false);
  const [runtimeRunning, setRuntimeRunning] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [validationResult, setValidationResult] = useState<WorkflowNotificationContentValidationResponse | null>(null);

  useEffect(() => {
    let cancelled = false;
    setSettings(null);
    setSchemaSource('active');
    setSchemaState(workflowIntegrationId ? 'Loading integration schema...' : 'No integration association. Payload paths require an example or a known variable path.');
    if (workflowIntegrationId) inboundSchemasApi.list(workflowIntegrationId).then(result => {
      if (cancelled) return;
      setSettings(result.settings);
      setSchemaState(effectiveOutputSchema(result.settings) ? '' : 'No active output schema. Draft paths are design-only.');
    }).catch(() => {
      if (!cancelled) setSchemaState('Integration schema unavailable or permission denied. Message editing and known variable paths remain available.');
    });
    return () => { cancelled = true; };
  }, [workflowIntegrationId]);

  const schema = schemaSource === 'active' ? effectiveOutputSchema(settings) : settings?.draftOutputSchemaJson;
  const bindings = schemaSource === 'active' ? effectiveRuntimeBindings(settings) : settings?.draftRuntimeBindingsJson;
  let schemaPaths: string[] = [];
  let schemaError = '';
  try { if (schema) schemaPaths = schemaContentPaths(schema); }
  catch { schemaError = 'Output schema could not be read. Use an example or a known variable path.'; }
  let example = { variables: { 'event.type': sourceType } as Record<string, string>, paths: [] as string[] };
  let exampleError = '';
  try { example = exampleContentVariables(payload, sourceType, bindings ?? '', context); }
  catch (error) { exampleError = error instanceof Error ? error.message : 'Invalid example JSON.'; }
  for (const name of globalVariableNames)
    example.variables[`global.${name}`] ??= '';
  const catalog = contentCatalog(sourceType, workflowSteps, currentStepOrder, [...schemaPaths, ...example.paths]);
  for (const name of globalVariableNames) {
    const path = `global.${name}`;
    if (!catalog.some(entry => entry.path === path))
      catalog.push({ path, category: 'Global', note: 'Execution-scoped value shared with awaited sub-workflows and reset for each root run.' });
  }
  for (const path of Object.keys(example.variables)) {
    if (path.startsWith('step.') || path.startsWith('integration.')) continue;
    if (!catalog.some(entry => entry.path === path)) catalog.push({ path, category: 'Payload', note: 'User-supplied example key; not proof of live availability.' });
  }
  const filtered = catalog.filter(entry => (category === 'All' || category === entry.category) && entry.path.toLowerCase().includes(search.toLowerCase()));
  const bodyFields = Array.from(new Set([
    ...catalog.filter(entry => entry.category === 'Payload').map(entry => entry.path),
    ...(config.bodySourceField ? [config.bodySourceField] : []),
  ])).filter(validVariablePath).sort();
  const subject = expandContent(config.subjectTemplate?.trim() ? config.subjectTemplate : '{{event.title}}', example.variables);
  const exampleBodyTemplate = config.bodySourceField
    ? config.bodyFormat === 'Html'
      ? example.variables[config.bodySourceField] ?? `{{${config.bodySourceField}}}`
      : `{{${config.bodySourceField}}}`
    : config.bodyTemplate?.trim() ? config.bodyTemplate : config.templateOverride?.trim() ? config.templateOverride : '{{event.description}}';
  const body = expandContent(exampleBodyTemplate, example.variables);
  const missing = Array.from(new Set([...subject.missing, ...body.missing]));
  const attachments = config.attachments ?? [];
  const sendAttachmentsIfPresent = config.sendAttachmentsIfPresent ?? true;
  const doNotSendAttachments = config.doNotSendAttachments ?? false;
  const updateAttachment = (index: number, patch: Partial<NotificationAttachmentMapping>) =>
    onChange({ ...config, attachments: attachments.map((item, itemIndex) => itemIndex === index ? { ...item, ...patch } : item) });

  const remember = (field: InsertField, element: HTMLInputElement | HTMLTextAreaElement) => {
    selection.current = { field, start: element.selectionStart ?? 0, end: element.selectionEnd ?? 0 };
    setTarget(field);
  };
  const insert = (path: string, field: InsertField = target) => {
    if (!validVariablePath(path)) return;
    const text = config[field] ?? '';
    const range = selection.current.field === field ? selection.current : { start: text.length, end: text.length };
    const next = insertContentToken(text, path, range.start, range.end);
    onChange(field === 'subjectTemplate' || field === 'bodyTemplate'
      ? updateMessageContent(config, field, next.text)
      : { ...config, [field]: next.text });
    selection.current = { field, start: next.caret, end: next.caret };
    setTarget(field);
    requestAnimationFrame(() => {
      const element = field === 'subjectTemplate' ? subjectRef.current
        : field === 'bodyTemplate' ? bodyRef.current : recipientRefs.current[field];
      element?.focus();
      element?.setSelectionRange(next.caret, next.caret);
    });
  };
  const copy = async (path: string) => {
    try { await navigator.clipboard.writeText(`{{${path}}}`); setCopyState(`Copied ${path}`); }
    catch { setCopyState('Copy unavailable. Insert the token into the selected field.'); }
  };

  const buildValidationPayload = (useLatestWorkflowInstance: boolean) => ({
    workflowId: useLatestWorkflowInstance && workflowId ? workflowId : undefined,
    useLatestWorkflowInstance,
    productionEmail: true,
    title: example.variables['event.title'] || example.variables['notification.title'] || example.variables['alert.title'] || 'Example title',
    description: example.variables['event.description'] || example.variables['notification.description'] || example.variables['alert.description'] || '',
    status: example.variables['event.status'] || example.variables['notification.status'] || example.variables['alert.status'] || 'Open',
    severity: example.variables['event.severity'] || example.variables['notification.severity'] || example.variables['alert.severity'] || 'Medium',
    service: example.variables['event.service'] || example.variables['notification.service'] || example.variables['alert.service'] || 'Unknown',
    source: example.variables['event.source'] || example.variables['notification.source'] || example.variables['alert.source'] || 'Workflow',
    magicLinkUrl: '',
    config,
    variables: example.variables,
  });

  const runValidation = async (useLatestWorkflowInstance: boolean) => {
    setValidationError('');
    setValidationResult(null);
    if (useLatestWorkflowInstance && !workflowId) {
      setValidationError('Save the workflow first to run runtime validation from the latest workflow instance.');
      return;
    }

    if (useLatestWorkflowInstance) setRuntimeRunning(true);
    else setValidationRunning(true);

    try {
      const result = await tenantApi.validateWorkflowNotificationContent(buildValidationPayload(useLatestWorkflowInstance));
      setValidationResult(result);
      if (!result.isValid && !result.error && result.unresolvedTokens.length > 0) {
        setValidationError(`Unresolved tokens: ${result.unresolvedTokens.join(', ')}`);
      }
    } catch (error) {
      setValidationError(error instanceof Error ? error.message : 'Validation failed.');
    } finally {
      if (useLatestWorkflowInstance) setRuntimeRunning(false);
      else setValidationRunning(false);
    }
  };
  const renderVariable = (entry: ContentVariable) => (
    <div className="workflow-content-variable" key={entry.path}>
      <code title={entry.note}>{`{{${entry.path}}}`}</code>
      <button type="button" className="btn-icon" aria-label={`Copy ${entry.path}`} title={`Copy ${entry.path}`} onClick={() => void copy(entry.path)}><Copy size={16} /></button>
      <button type="button" className="btn-icon" aria-label={`Insert ${entry.path}`} title={`Insert into ${target === 'subjectTemplate' ? 'Subject' : target === 'bodyTemplate' ? 'Body' : target === 'toEmails' ? 'To' : target === 'ccEmails' ? 'CC' : 'BCC'}`} onClick={() => insert(entry.path)}><Plus size={16} /></button>
    </div>
  );

  return <section className="workflow-message-content" aria-label="Message Content">
    <div className="workflow-recipient-actions">
    {recipientAction}
    <label className="workflow-recipient-override" htmlFor={`${id}-exclude-channel-recipients`}>
      <input id={`${id}-exclude-channel-recipients`} type="checkbox" role="switch"
        checked={config.excludeChannelEmailRecipients ?? false}
        aria-describedby={`${id}-recipient-mode-help`}
        onChange={event => onChange({ ...config, excludeChannelEmailRecipients: event.target.checked })} />
      <span>Exclude channel default email recipients</span>
    </label>
    </div>
    <p id={`${id}-recipient-mode-help`} className="help-text">
      {config.excludeChannelEmailRecipients
        ? 'Email channels use only the To, CC and BCC below. No fallback to channel defaults if these resolve empty. Explicit User, Group and On-call targets are unchanged.'
        : 'To, CC and BCC are added to the email channel defaults. Enable the switch to exclude those defaults.'}
    </p>
    <h4>Message Content</h4>
    <div className="workflow-content-layout">
      <div className="workflow-content-fields">
    <div className="workflow-recipient-fields" aria-label="Notification recipients">
      {([
        ['toEmails', 'To', config.toEmails ?? config.adHocEmails ?? ''],
        ['ccEmails', 'CC', config.ccEmails ?? ''],
        ['bccEmails', 'BCC', config.bccEmails ?? ''],
      ] as const).map(([field, label, value]) => <label key={field} htmlFor={`${id}-${field}`}>
        <span>{label}</span>
        <input id={`${id}-${field}`} type="text" ref={element => { recipientRefs.current[field] = element; }} value={value}
          placeholder={label === 'To' ? 'Optional when CC or BCC is set' : label === 'BCC' ? 'Optional, hidden from all recipients' : 'Optional'}
          onFocus={event => remember(field, event.currentTarget)} onSelect={event => remember(field, event.currentTarget)}
          onChange={event => onChange({ ...config, [field]: event.target.value, ...(field === 'toEmails' ? { adHocEmails: undefined } : {}) })} />
      </label>)}
    </div>
    <p className="help-text">Separate recipients with commas or semicolons. Variable tokens are resolved when the workflow runs. At least one recipient is needed across To, CC and BCC; To may be left empty. To and CC addresses are visible to every recipient. BCC addresses are hidden, so use BCC only to keep recipients from seeing each other.</p>
    <div className="form-group">
      <label htmlFor={`${id}-subject`}>Subject</label>
      <textarea id={`${id}-subject`} ref={subjectRef} rows={2} value={config.subjectTemplate ?? ''} placeholder="Default: normalised title"
        onFocus={event => remember('subjectTemplate', event.currentTarget)} onSelect={event => remember('subjectTemplate', event.currentTarget)}
        onChange={event => onChange(updateMessageContent(config, 'subjectTemplate', event.target.value))} />
    </div>
    <div className="form-group">
      <label htmlFor={`${id}-body`}>Body</label>
      <div className="workflow-body-source-controls">
        <div>
          <label htmlFor={`${id}-body-format`}>Body format</label>
          <select id={`${id}-body-format`} value={config.bodyFormat ?? 'PlainText'}
            onChange={event => onChange({ ...config, bodyFormat: event.target.value as 'PlainText' | 'Html' })}>
            <option value="PlainText">Plain text</option>
            <option value="Html">HTML</option>
          </select>
        </div>
        <div>
          <label htmlFor={`${id}-body-field`}>Field</label>
          <select id={`${id}-body-field`} value={config.bodySourceField ?? ''}
            onChange={event => {
              const path = event.target.value;
              onChange({ ...config, bodySourceField: path || undefined,
                bodyTemplate: path ? `{{${path}}}` : config.bodyTemplate });
            }}>
            <option value="">Custom template / default</option>
            {bodyFields.map(path => <option key={path} value={path}>{`{{${path}}}`}</option>)}
          </select>
        </div>
      </div>
      <textarea id={`${id}-body`} ref={bodyRef} rows={5} value={config.bodyTemplate ?? ''}
        placeholder={config.bodyFormat === 'Html' ? 'Default: /body, then /details from normalized payload' : 'Default: normalised description'}
        onFocus={event => remember('bodyTemplate', event.currentTarget)} onSelect={event => remember('bodyTemplate', event.currentTarget)}
        onChange={event => onChange(updateMessageContent(config, 'bodyTemplate', event.target.value))} />
      {config.bodySourceField && <p className="help-text">
        {config.bodyFormat === 'Html'
          ? 'The selected field supplies the HTML template. Individual variable values are escaped and the resulting HTML is sanitized.'
          : 'The selected field supplies the whole body as a plain-text variable token.'}
        {' '}Editing the body switches back to a custom template.
      </p>}
      {config.bodyFormat === 'Html' && <p className="help-text">Use {'{{event.data.normalisedPayload.field}}'} for individual fields. Variable values are HTML-escaped and unsafe markup is removed before delivery. Non-email channels receive text extracted from the HTML.</p>}
    </div>
    {config.templateOverride && <p className="help-text">Legacy body override retained.</p>}
    <fieldset className="workflow-attachments" aria-label="Email attachments">
      <legend>Attachments</legend>
      <div className="workflow-attachment-modes">
        <label htmlFor={`${id}-attachments-if-present`}>
          <input id={`${id}-attachments-if-present`} type="checkbox"
            checked={sendAttachmentsIfPresent} disabled={doNotSendAttachments}
            onChange={event => onChange({ ...config, sendAttachmentsIfPresent: event.target.checked })} />
          <span>Send attachments if they exist</span>
        </label>
        <label htmlFor={`${id}-attachments-none`}>
          <input id={`${id}-attachments-none`} type="checkbox" checked={doNotSendAttachments}
            onChange={event => onChange({ ...config, doNotSendAttachments: event.target.checked })} />
          <span>Do not send attachments</span>
        </label>
      </div>
      {attachments.map((attachment, index) => <div className="workflow-attachment-row" key={index}>
        <label htmlFor={`${id}-attachment-field-${index}`}>
          <span>Field</span>
          <select id={`${id}-attachment-field-${index}`} value={attachment.sourceField}
            onChange={event => updateAttachment(index, { sourceField: event.target.value })}>
            <option value="">Select a payload field</option>
            {Array.from(new Set([...bodyFields, ...(attachment.sourceField ? [attachment.sourceField] : [])])).sort()
              .map(path => <option key={path} value={path}>{`{{${path}}}`}</option>)}
          </select>
        </label>
        <label htmlFor={`${id}-attachment-name-${index}`}>
          <span>File name</span>
          <input id={`${id}-attachment-name-${index}`} type="text" value={attachment.fileName ?? ''} maxLength={128}
            placeholder="Auto (invite.ics for calendar data)"
            onChange={event => updateAttachment(index, { fileName: event.target.value || undefined })} />
        </label>
        <label htmlFor={`${id}-attachment-type-${index}`}>
          <span>Content type</span>
          <input id={`${id}-attachment-type-${index}`} type="text" value={attachment.contentType ?? ''} maxLength={128}
            placeholder="Auto from file name"
            onChange={event => updateAttachment(index, { contentType: event.target.value || undefined })} />
        </label>
        <button type="button" className="btn-icon" aria-label={`Remove attachment ${index + 1}`} title="Remove attachment"
          onClick={() => onChange({ ...config, attachments: attachments.filter((_, item) => item !== index) })}><X size={16} /></button>
      </div>)}
      <button type="button" className="btn-secondary btn-sm" disabled={attachments.length >= MAX_ATTACHMENTS}
        onClick={() => onChange({ ...config, attachments: [...attachments, { sourceField: '' }] })}><Plus size={16} /> Add attachment</button>
      <p className="help-text">
        {doNotSendAttachments
          ? 'Attachments are disabled for this step, even when the mapped fields have values.'
          : sendAttachmentsIfPresent
            ? 'Each mapped field is attached when it has a value; empty or missing fields are skipped. Values may be raw text (for example BEGIN:VCALENDAR) or base64.'
            : 'Attachments are not sent. Enable "Send attachments if they exist" to include mapped fields.'}
        {' '}Email channels only. Up to {MAX_ATTACHMENTS} files, {MAX_ATTACHMENT_KB} KB in total.
      </p>
    </fieldset>
    <div className="workflow-content-toolbar">
      <div className="workflow-content-segments" role="group" aria-label="Insert target">
        {(['toEmails', 'ccEmails', 'bccEmails', 'subjectTemplate', 'bodyTemplate'] as InsertField[]).map(field => <button type="button" key={field} aria-pressed={target === field} onClick={() => setTarget(field)}>{field === 'subjectTemplate' ? 'Subject' : field === 'bodyTemplate' ? 'Body' : field === 'toEmails' ? 'To' : field === 'ccEmails' ? 'CC' : 'BCC'}</button>)}
      </div>
      <button type="button" className="btn-secondary btn-sm" onClick={() => insert('event.data.normalisedPayload', 'bodyTemplate')}><Plus size={16} /> Whole normalised JSON</button>
      <button type="button" className="btn-secondary btn-sm" disabled={validationRunning || runtimeRunning} onClick={() => void runValidation(false)}>
        {validationRunning ? 'Validating...' : config.bodyFormat === 'Html' ? 'Preview HTML' : 'Validate Content'}
      </button>
      <button type="button" className="btn-secondary btn-sm" disabled={validationRunning || runtimeRunning || !workflowId} onClick={() => void runValidation(true)}>
        {runtimeRunning ? 'Testing...' : 'Runtime Test (Latest Instance)'}
      </button>
    </div>
      </div>
      <section className="workflow-content-picker" aria-label="Variable insertion toolbar">
        <header className="workflow-content-picker-header">
          <h5>Variables</h5>
          <label htmlFor={`${id}-search`}>Search variables</label>
          <input id={`${id}-search`} type="search" value={search} onChange={event => setSearch(event.target.value)} />
          <div className="workflow-content-segments" role="group" aria-label="Variable category">
            {['All', 'Common', 'Alert', 'Notification', 'Integration', 'Global', 'Payload', 'Steps'].map(item => <button type="button" key={item} aria-pressed={category === item} onClick={() => setCategory(item)}>{item}</button>)}
          </div>
          {settings && <div className="workflow-content-segments" role="group" aria-label="Schema source">
            <button type="button" aria-pressed={schemaSource === 'active'} onClick={() => setSchemaSource('active')}>Active schema</button>
            <button type="button" aria-pressed={schemaSource === 'draft'} onClick={() => setSchemaSource('draft')}>Draft (design only)</button>
          </div>}
          <p className="help-text" role="status">{schemaState || (schemaSource === 'draft' ? 'Draft paths: not available live until activation.' : 'Active declared paths; optional fields may be absent.')}</p>
          {schemaError && <p role="status">{schemaError}</p>}
        </header>
        <div className="workflow-content-variable-list" aria-label="Available variables">
          {filtered.map(renderVariable)}{!filtered.length && <p>No matching variables.</p>}
        </div>
        <footer className="workflow-content-picker-footer">
          <p className="help-text">Arrays use numeric dotted indexes. Schema index 0 is illustrative; example JSON supplies actual indexes. Dynamic keys and conditional step outputs may be absent.</p>
          <label htmlFor={`${id}-path`}>Use variable path</label>
          <div className="workflow-content-manual">
            <input id={`${id}-path`} value={manualPath} onChange={event => setManualPath(event.target.value)} placeholder="event.data.normalisedPayload.company.name" />
            <button type="button" className="btn-icon" aria-label="Insert variable path" title="Insert variable path" disabled={!validVariablePath(manualPath.trim())} onClick={() => insert(manualPath.trim())}><Plus size={18} /></button>
          </div>
          <p className="help-text" role="status">{copyState}</p>
        </footer>
      </section>
    </div>
    {validationError && <p role="alert">{validationError}</p>}
    {validationResult && <div className="workflow-content-validation" role="status">
      <p>{validationResult.isValid ? 'Validation passed.' : `Validation failed: ${validationResult.error || 'Unknown error'}`}</p>
      {validationResult.unresolvedTokens.length > 0 && <p>Unresolved: {validationResult.unresolvedTokens.join(', ')}</p>}
      {validationResult.subjectPreview && <><h5>Server Subject Preview</h5><pre>{validationResult.subjectPreview}</pre></>}
      {validationResult.bodyPreview && <><h5>Server Body Preview</h5><pre>{validationResult.bodyPreview}</pre></>}
      {config.bodyFormat === 'Html' && validationResult.isValid && validationResult.bodyHtmlPreview &&
        <div className="workflow-content-html-preview">
          <h5>Rendered HTML Preview</h5>
          <iframe
            title="Rendered email HTML preview"
            sandbox=""
            srcDoc={`<!doctype html><meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src data:; style-src 'unsafe-inline'; font-src data:">${validationResult.bodyHtmlPreview}`}
          />
        </div>}
      {validationResult.runtimeVariablesUsed && <p>Runtime variables source: latest workflow instance{validationResult.runtimeInstanceId ? ` (${validationResult.runtimeInstanceId})` : ''}.</p>}
      {(validationResult.attachmentPreview?.length ?? 0) > 0 && <>
        <h5>Attachments</h5>
        <ul className="workflow-attachment-preview">
          {validationResult.attachmentPreview!.map(item => <li key={`${item.sourceField}-${item.fileName}`}>
            {item.status === 'Attached'
              ? `${item.fileName} (${item.contentType}, ${formatBytes(item.sizeBytes)})`
              : `{{${item.sourceField}}} has no value; skipped`}
          </li>)}
        </ul>
      </>}
    </div>}
    <details className="workflow-content-preview">
      <summary>Local Example Preview</summary>
      <p className="help-text">Partial example context, not a live delivery preview. No message is sent. Missing runtime values remain unresolved.</p>
      <label htmlFor={`${id}-payload`}>Example normalised JSON</label>
      <textarea id={`${id}-payload`} rows={5} value={payload} placeholder="{}" onChange={event => setPayload(event.target.value)} />
      <label htmlFor={`${id}-context`}>Example variable values (JSON string map)</label>
      <textarea id={`${id}-context`} rows={3} value={context} placeholder={'{"event.title":"Example title"}'} onChange={event => setContext(event.target.value)} />
      <p className="help-text">{bindings ? `${schemaSource === 'active' ? 'Active' : 'Draft design-only'} runtime bindings project example fields. Other IDs, timestamps and step outputs require example values.` : 'No runtime bindings loaded. Supply explicit example values for title, description and other context fields.'}</p>
      {exampleError ? <p role="alert">{exampleError}</p> : <>
        <h5>Subject Preview</h5><pre data-testid="subject-preview">{subject.text}</pre>
        <h5>Body Preview</h5><pre data-testid="body-preview">{body.text}</pre>
        <p role="status">{missing.length ? `Unresolved: ${missing.join(', ')}` : 'All tokens resolved in this example only.'}</p>
      </>}
    </details>
  </section>;
};